import { dayjs, WarningIcon } from '@umanforge/common';
import { Warning } from 'interfaces/warning.interface';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface PlanningWarningTileProps {
  className?: string;
  warning: Warning;
  onClick: ( taskId: string ) => void
}

const PlanningWarningTileComponent = ( {
  className = '',
  warning,
  onClick
} : PlanningWarningTileProps ) => {
  const { t } = useTranslation();

  const [ title, setTitle ] = useState<string>( warning.taskName );
  const [ description, setDescription ] = useState<string>( warning.objects );
  const [ icon, ] = useState<React.ReactElement>( <WarningIcon className='right text-orange-500 w-10 h-10 '/> );

  useEffect( () => {

    switch ( warning.type ) {
    case 'absence':
      if ( warning.objects.split( ', ' ).length > 1 ) {
        setTitle( warning.objects );
        setDescription( t( 'translation:are_absent_on_the_task_date', { taskName: warning.taskName, date: dayjs( warning.date ).format( 'LL' ) } ) );
      }
      else {
        setTitle( warning.objects );
        setDescription( t( 'translation:is_absent_on_the_task_date', { taskName: warning.taskName, fullname: warning.objects, date: dayjs( warning.date ).format( 'LL' ) } ) );
      }
      break;
    case 'user_required':
      if ( Number.parseInt( warning.objects ) > 1 ) {
        setTitle( warning.objects + ' ' + t( 'users_required' ) );
        setDescription( `${ t( 'translation:users_required_on_task', {
          fullname: warning,
          taskName: warning.taskName,
          date: dayjs( warning.date ).format( 'LL' )
        } ) }` );
      }
      else {
        setTitle( warning.objects + ' ' + t( 'user_required' ) );
        setDescription( `${ t( 'translation:user_required_on_task', {
          fullname: warning,
          taskName: warning.taskName,
          date: dayjs( warning.date ).format( 'LL' )
        } ) }` );
      }
      break;
    case 'user_stacked':
      setTitle( warning.objects );
      setDescription( t( 'translation:one_or_more_users_are_assigned_to_multiple_tasks_at_the_same_time', { taskName: warning.taskName, date: dayjs( warning.date ).format( 'LL' ) } ) );
      break;
    case 'equipment_stacked':
      setTitle( warning.objects );
      setDescription( t( 'translation:one_or_more_equipments_are_assigned_to_multiple_tasks_at_the_same_time', { taskName: warning.taskName, date: dayjs( warning.date ).format( 'LL' ) } ) );
      break;
    case 'ability_expiration':
      setTitle( t( 'translation:the_authorization_of_user_expire_the', { abilityName: warning.taskName, userName: warning.objects, date: dayjs( warning.date ).format( 'LL' ) } ) );
      setDescription( '' );
      break;
    case 'equipment_expiration':
      setTitle( t( 'translation:the_equipment_expire_the', { equipmentName: warning.objects, date: dayjs( warning.date ).format( 'LL' ) } ) );
      setDescription( '' );
      break;
    default: return;
    }
  }, [] );

  return (
    <div
      className={ className + ' group relative w-full min-h-12 min-w-0 overflow-hidden rounded-xl border border-orange-200 p-2.5 pl-3 flex items-center gap-3 bg-white cursor-pointer transition-all duration-200 ease-out hover:-translate-y-0.5 hover:border-orange-300 hover:shadow-[0_8px_16px_rgba(15,23,42,0.06)]' }
      onClick={ () => { onClick( warning.taskId ); } }
    >
      <div className='absolute inset-y-0 left-0 w-1.5 bg-orange-400' />
      <div className='w-full min-w-0 overflow-hidden pl-1'>
        <p className='text-sm font-bold text-slate-800 break-words whitespace-normal overflow-wrap-anywhere'> { title } </p>
        <p className='mt-0.5 text-sm text-slate-700 break-words whitespace-normal overflow-wrap-anywhere'> { description } </p>
      </div>
      <div className='shrink-0 flex h-10 w-10 items-center justify-center rounded-lg border border-orange-200 bg-orange-50 transition-transform duration-200 group-hover:scale-105'>
        { icon }
      </div>
    </div>
  );
};

export default PlanningWarningTileComponent;